// dSpr Self-Review Form - `/self-review?r=<reviewId>`
//
// Public-facing standalone page. App.js suppresses LeftNavbar for any path
// starting with /self-review so this renders full-bleed on the cream
// background per the mockup.
//
// Visual structure mirrors dSPR_form_mockup.html - brand crest + cycle pill,
// 2-step stepper, white cards with cream-tinted headers, 4 colored section
// cards. Fraunces serif for headers, Hanken Grotesk for body, gold accents.
//
// Auth flow: /check → OTP modal blocks the page until verified → Page 1 →
// Page 2. Page 1 is rendered behind the OTP modal so the user sees what
// they're verifying for, but its action buttons stay disabled until OTP
// success.
//
// Phase A scope: form rendering + auto-save. Upload widgets are visually
// present (matching the mockup) but click is a no-op toast - Q7 lean A is
// preserved in spirit (no upload data flows) while the visual matches the
// mockup. Submit button is disabled.
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
// Swal removed from the review form - inline <Banner> + native window.confirm
// handle status messages and confirmations to keep the dark/orange theme
// intact. Other dadmin pages still use Swal.
import { apiFetch } from "./utils/api";
import { fmtDate as fmtDateShort, fmtDateRange, fmtDateLong } from "./utils/dSpr_DateFormat";
import {
    DSPR_QUESTION_SCHEMA,
    CLIENT_PROJECT_FALLBACK,
} from "./dSpr_question_schema";
import "./dSpr_ReviewForm.css";
import logoEagle from "./assets/img/logo_eagle.png";

const AUTO_SAVE_DEBOUNCE_MS = 5000;
const GOOGLE_FONTS_HREF =
    "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Hanken+Grotesk:wght@400;500;600;700&display=swap";

// Inject Google Fonts once
function useFonts() {
    useEffect(() => {
        if (document.getElementById("dspr-rf-fonts")) return;
        const link = document.createElement("link");
        link.id = "dspr-rf-fonts";
        link.rel = "stylesheet";
        link.href = GOOGLE_FONTS_HREF;
        document.head.appendChild(link);
    }, []);
}

// Date formatters (fmtDateShort, fmtDateRange) live in
// src/utils/dSpr_DateFormat.js - imported above.

// Does this response actually contain user content? Used to mark unfilled
// questions with a "Not answered" badge + drive the footer/section counters.
// Mirrors the same logic in dSpr_GenerateReviewPdf.js (PDF) and Reports drawer.
function isAnswered(question, response) {
    if (!response) return false;
    switch (question.type) {
        case "text":
        case "text_with_upload":
            return !!(response.value && String(response.value).trim());
        case "single_choice":
            return typeof response.choice === "number";
        case "single_choice_with_text":
        case "single_choice_with_text_and_upload":
            return typeof response.choice === "number"
                || !!(response.text && String(response.text).trim());
        case "rating_1_to_5_with_text":
            return typeof response.rating === "number";
        case "number_pair_with_text":
            return typeof response.num1 === "number"
                || typeof response.num2 === "number"
                || !!(response.text && String(response.text).trim());
        default:
            return false;
    }
}

function relativeTimeFromNow(when) {
    if (!when) return null;
    const diffMs = Date.now() - new Date(when).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 5) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hr ago`;
    return fmtDateShort(new Date(when).toISOString().slice(0, 10));
}

// ════════════════════════════════════════════════════════════════════════════
export default function DSprReviewForm() {
    useFonts();
    const [searchParams] = useSearchParams();
    const reviewId = searchParams.get("r");

    const [step, setStep] = useState("loading");      // loading | check_failed | not_started | already_submitted | prereq | form | thank_you | draft_saved | readonly
    const [awaitingOtp, setAwaitingOtp] = useState(true);
    // OTP sub-stage tracked here so the Stepper at the top can reflect it:
    //   false → user still on email-entry screen → "Verify Email" is ACTIVE
    //   true  → email submitted, user on code-entry → "Enter OTP" is ACTIVE
    const [otpSent, setOtpSent] = useState(false);

    const [reviewMeta, setReviewMeta] = useState(null);
    const [token, setToken] = useState(null);
    const [session, setSession] = useState(null);
    const [responses, setResponses] = useState({});
    const [attachmentsByQ, setAttachmentsByQ] = useState({});  // { q6: [...], q10: [...], q12: [...] }
    const [savedAt, setSavedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [clientProject, setClientProject] = useState("");
    // Free-text input shown when clientProject === "Other". Persisted alongside
    // value in the __client_project response so HR sees the actual project,
    // not just "Other".
    const [clientProjectOther, setClientProjectOther] = useState("");
    const [clientProjectOptions, setClientProjectOptions] = useState(CLIENT_PROJECT_FALLBACK);
    const [thankYouData, setThankYouData] = useState(null);    // populated on successful submit

    // Fetch dynamic Client/Project options once on mount. The GET endpoint
    // is open (no auth) - these are just dropdown strings. Falls back to
    // the hardcoded list if the API errors so the form never breaks.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/dspr/client-project-options");
                const data = await res.json();
                if (res.ok && Array.isArray(data) && data.length) {
                    setClientProjectOptions(data.map(o => o.name));
                }
            } catch { /* tolerated - fallback list still works */ }
        })();
    }, []);

    useEffect(() => {
        if (!reviewId) { setStep("check_failed"); return; }
        (async () => {
            try {
                const res = await apiFetch(`/api/dspr/review/check?r=${encodeURIComponent(reviewId)}`);
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || "Couldn't load review");
                setReviewMeta(data);
                // Status branching:
                //   Submitted / Approved → "Already submitted" screen (no OTP)
                //   Not Started           → "Not yet initiated" screen (HR
                //                           hasn't fired the request email yet,
                //                           so the form isn't open. Employee
                //                           shouldn't even have this URL -
                //                           if they do, they reached it from
                //                           somewhere odd.)
                //   Initiated / In Progress → Normal OTP → Page 1 flow
                if (data.status === "Submitted" || data.status === "Approved") {
                    setStep("already_submitted");
                    setAwaitingOtp(false);
                } else if (data.status === "Not Started") {
                    setStep("not_started");
                    setAwaitingOtp(false);
                } else {
                    setStep("prereq");
                    setAwaitingOtp(true);
                }
            } catch (e) {
                setReviewMeta({ error: e.message });
                setStep("check_failed");
            }
        })();
    }, [reviewId]);

    // ── Cross-tab submission lockout ────────────────────────────────────────
    // While the user is filling the form, poll GET /status every 8 seconds.
    // If we discover someone else (admin proxy or the employee in another
    // tab) just submitted this review, flip our UI straight to the
    // "already submitted" screen so the local draft can't be re-submitted
    // over the top of the durable record. Polling stops as soon as the
    // form is no longer in an editable step.
    useEffect(() => {
        if (!token) return;
        if (step !== "form" && step !== "prereq" && step !== "draft_saved") return;
        let cancelled = false;
        const check = async () => {
            try {
                const res = await apiFetch("/api/dspr/review/status", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                if (data.status === "Submitted" || data.status === "Approved") {
                    // If THIS browser session submitted it (very recent local
                    // submit racing with the next poll), let the existing
                    // onSubmitted flow handle the ThankYou screen - don't
                    // overwrite it. submitted_by_me distinguishes the two.
                    if (!data.submitted_by_me) {
                        setReviewMeta(prev => prev ? { ...prev, submitted_at: data.submitted_at } : prev);
                        setStep("already_submitted");
                    }
                } else if (data.access_error) {
                    // Something changed under us mid-fill: HR closed the cycle,
                    // soft-deleted / deactivated the employee, or revoked their
                    // dSpr access. The backend hands back the exact reason;
                    // flip to check_failed so the form stops accepting input.
                    // (Writes are hard-403'd server-side regardless.)
                    setReviewMeta(prev => ({
                        ...(prev || {}),
                        error: data.access_error,
                    }));
                    setStep("check_failed");
                }
            } catch { /* network blip - try again on next tick */ }
        };
        // Run immediately so a stale tab returning from background catches up
        // without waiting a full poll interval.
        check();
        const id = setInterval(check, 8000);
        return () => { cancelled = true; clearInterval(id); };
    }, [token, step]);

    const onAuthenticated = async (tok, sessionData) => {
        setToken(tok);
        setSession(sessionData);
        setAwaitingOtp(false);
        try {
            const res = await apiFetch("/api/dspr/review/draft", {
                headers: { Authorization: `Bearer ${tok}` },
            });
            const data = await res.json();
            if (res.ok) {
                setResponses(data.responses || {});
                setAttachmentsByQ(data.attachments_by_q || {});
                setSavedAt(data.saved_at);
                if (data.responses?.__client_project?.value) {
                    setClientProject(data.responses.__client_project.value);
                    if (data.responses.__client_project.custom) {
                        setClientProjectOther(data.responses.__client_project.custom);
                    }
                }
            }
        } catch { /* tolerated */ }
        if (sessionData.review.status === "Submitted" || sessionData.review.status === "Approved") {
            setStep("readonly");
        }
    };

    if (step === "loading") {
        return (
            <div className="dspr-rf-page">
                <Bg />
                <div className="dspr-rf-wrap">
                    <BrandHeader halfLabel="" />
                    <div className="dspr-rf-loading">Loading your review…</div>
                </div>
            </div>
        );
    }
    if (step === "check_failed") {
        // Auto-linkify "admin@dolluzcorp.com" inside the backend error string
        // so it matches the orange link style above instead of rendering as
        // plain text. Covers all three gate messages (inactive, removed,
        // app_dSpr revoked) with one helper.
        const renderErrorWithMailLink = (text) => {
            const parts = String(text || "").split(/(admin@dolluzcorp\.com)/);
            return parts.map((part, i) =>
                part === "admin@dolluzcorp.com"
                    ? <a key={i} href="mailto:admin@dolluzcorp.com" className="dspr-rf-mail-link">{part}</a>
                    : <React.Fragment key={i}>{part}</React.Fragment>
            );
        };
        return (
            <div className="dspr-rf-page">
                <Bg />
                <div className="dspr-rf-wrap">
                    <BrandHeader halfLabel="" />
                    <div className="dspr-rf-error-card">
                        <h2>We couldn't find your review</h2>
                        {/* Show ONE line only - either the specific server reason
                            (inactive / removed / app_dSpr revoked) when known, or the
                            generic fallback when the request failed without a clear
                            reason. Two lines used to repeat the contact email twice. */}
                        {reviewMeta?.error
                            ? <p className="dspr-rf-error-detail">{renderErrorWithMailLink(reviewMeta.error)}</p>
                            : <p>The link may be invalid or expired. Please contact <a href="mailto:admin@dolluzcorp.com" className="dspr-rf-mail-link">admin@dolluzcorp.com</a>.</p>
                        }
                    </div>
                </div>
            </div>
        );
    }

    // Submit success → render Thank You. The token stays valid (still 2h
    // remaining) so the user can re-download the PDF without re-OTP.
    const onSubmitted = (data) => {
        setThankYouData(data);
        setStep("thank_you");
    };

    const halfLabel = reviewMeta?.cycle?.half_label || session?.cycle?.half_label || "";
    const cycle = session?.cycle || reviewMeta?.cycle || null;
    const stepperState =
        step === "form" || step === "thank_you" ? (step === "thank_you" ? "done" : "form") :
        step === "readonly" ? "done" : "prereq";

    // OTP is a STANDALONE FULL SCREEN - no page content rendered underneath.
    // Match dEpr's flow: status check → already-submitted screen / not-started
    // screen short-circuits, otherwise OTP fills the viewport on its own,
    // and only AFTER verify do the prereq/form screens render.
    if (step === "already_submitted") {
        return (
            <div className="dspr-rf-page">
                <Bg />
                <div className="dspr-rf-wrap">
                    <BrandHeader halfLabel={halfLabel} />
                    <AlreadySubmittedScreen reviewMeta={reviewMeta} />
                </div>
            </div>
        );
    }
    if (step === "not_started") {
        return (
            <div className="dspr-rf-page">
                <Bg />
                <div className="dspr-rf-wrap">
                    <BrandHeader halfLabel={halfLabel} />
                    <NotStartedScreen reviewMeta={reviewMeta} />
                </div>
            </div>
        );
    }
    if (awaitingOtp && reviewMeta) {
        // Standalone OTP - no other content rendered behind it.
        // Stepper state mirrors the OTP sub-stage so it reflects what the
        // user is actually doing on screen:
        //   email-entry → "Verify Email" active
        //   code-entry  → "Verify Email" done + "Enter OTP" active
        return (
            <div className="dspr-rf-page">
                <Bg />
                <div className="dspr-rf-wrap">
                    <BrandHeader halfLabel={halfLabel} />
                    <Stepper state={otpSent ? "otp" : "email"} />
                    <OtpModal
                        reviewId={reviewId}
                        reviewMeta={reviewMeta}
                        onAuthenticated={onAuthenticated}
                        onOtpSent={() => setOtpSent(true)}
                        onOtpReset={() => setOtpSent(false)}
                        inline
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="dspr-rf-page">
            <Bg />
            <div className="dspr-rf-wrap">
                {/* Brand header hidden on the form page - replaced by the
                    dark progress card inside FormPage. Shown on prereq /
                    thank-you / draft-saved / readonly to keep brand presence.
                    Stepper stays visible everywhere except draft-saved. */}
                {step !== "form" && <BrandHeader halfLabel={halfLabel} />}
                {step !== "draft_saved" && <Stepper state={stepperState} />}

                {step === "prereq" && (
                    <PrereqPage
                        cycle={cycle}
                        disabled={false}
                        onProceed={() => setStep("form")}
                    />
                )}

                {step === "form" && (
                    <FormPage
                        session={session}
                        token={token}
                        responses={responses}
                        setResponses={setResponses}
                        attachmentsByQ={attachmentsByQ}
                        setAttachmentsByQ={setAttachmentsByQ}
                        savedAt={savedAt}
                        setSavedAt={setSavedAt}
                        saving={saving}
                        setSaving={setSaving}
                        clientProject={clientProject}
                        setClientProject={setClientProject}
                        clientProjectOther={clientProjectOther}
                        setClientProjectOther={setClientProjectOther}
                        clientProjectOptions={clientProjectOptions}
                        onBack={() => setStep("prereq")}
                        onSubmitted={onSubmitted}
                        onSavedAndExit={() => setStep("draft_saved")}
                    />
                )}

                {step === "draft_saved" && (
                    <DraftSavedScreen
                        reviewMeta={reviewMeta}
                        onResume={() => setStep("form")}
                    />
                )}

                {step === "thank_you" && (
                    <ThankYouPage
                        session={session}
                        thankYou={thankYouData}
                    />
                )}

                {step === "readonly" && (
                    <ReadOnlyView
                        session={session}
                        responses={responses}
                        attachmentsByQ={attachmentsByQ}
                        clientProject={clientProject}
                        clientProjectOther={clientProjectOther}
                        token={token}
                    />
                )}
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// Banner - inline status message used throughout the review form INSTEAD of
// Swal popups. Swal would clash with the dark navy + white-card theme; an
// inline banner sits naturally inside the existing card flow.
//
//   kind: "error"   → red    (#DC2626) with ⚠️
//   kind: "warning" → amber  (#92400E) with ⚠️ on amber-50 card bg
//   kind: "success" → green  (#047857) with ✓
//   kind: "info"    → blue   default text color
//
// Auto-dismiss is the caller's responsibility (use setTimeout(..., 4000) for
// success/info; keep errors visible until next action).
function Banner({ kind, message, onDismiss }) {
    if (!message) return null;
    const isWarn = kind === "warning";
    const baseStyle = isWarn
        ? {
            background: "#FFFBEB", border: "1.5px solid #FDE68A",
            borderRadius: 10, padding: "14px 18px", fontSize: 13,
            color: "#92400E", marginBottom: 16, display: "flex",
            alignItems: "flex-start", gap: 8,
            animation: "fadeIn 0.3s ease",
        }
        : {
            textAlign: "center", fontSize: 13, marginBottom: 16,
            animation: "fadeIn 0.3s ease",
            color:
                kind === "error"   ? "#DC2626" :
                kind === "success" ? "#047857" :
                                     "#0D1B2A",
        };
    const icon =
        kind === "error"   ? "⚠️" :
        kind === "warning" ? "⚠️" :
        kind === "success" ? "✓"  :
                             "ℹ️";
    return (
        <div style={baseStyle} role={kind === "error" ? "alert" : "status"}>
            <span style={{ flexShrink: 0 }}>{icon}</span>
            <span style={{ flex: 1 }}>{message}</span>
            {onDismiss && (
                <button type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "inherit", fontSize: 16, padding: 0,
                        lineHeight: 1, marginLeft: 8,
                    }}>×</button>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// ConfirmDialog - themed yes/no popover used in place of window.confirm().
// Sits over the page on a slightly opaque navy backdrop with a sliding white
// card; orange primary button matches the form theme.
//
// Usage:
//   const [confirmState, setConfirmState] = useState(null);
//   setConfirmState({
//       title: "Remove this attachment?",
//       body: "This action cannot be undone.",
//       confirmLabel: "Remove",
//       danger: true,
//       onConfirm: () => doDelete(),
//   });
//   <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
function ConfirmDialog({ state, onClose }) {
    if (!state) return null;
    const {
        title = "Are you sure?",
        body,
        confirmLabel = "Confirm",
        cancelLabel = "Cancel",
        danger = false,
        onConfirm,
    } = state;
    const handleConfirm = () => {
        try { onConfirm && onConfirm(); }
        finally { onClose && onClose(); }
    };
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose && onClose();
    };
    return (
        <div
            onClick={handleBackdropClick}
            style={{
                position: "fixed", inset: 0, zIndex: 100,
                background: "rgba(13, 27, 42, 0.55)",
                backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 20,
                animation: "fadeIn 0.2s ease",
            }}>
            <div style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
                width: "100%",
                maxWidth: 420,
                overflow: "hidden",
                animation: "slideUp 0.25s cubic-bezier(.22,1,.36,1)",
            }}>
                <div style={{
                    padding: "26px 26px 16px",
                    background: danger
                        ? "linear-gradient(180deg, #FEF2F2 0%, #fff 100%)"
                        : "linear-gradient(180deg, #FFF8F5 0%, #fff 100%)",
                }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 14,
                        background: danger
                            ? "linear-gradient(135deg, #FECACA, #FCA5A5)"
                            : "linear-gradient(135deg, #FFF5F0, #FED7C3)",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        fontSize: 22, marginBottom: 12,
                    }} aria-hidden="true">
                        {danger ? "🗑️" : "❓"}
                    </div>
                    <h3 style={{
                        margin: 0, padding: 0,
                        fontFamily: "'Sora', sans-serif",
                        fontWeight: 700, fontSize: 18,
                        color: "#0D1B2A", lineHeight: 1.3,
                    }}>{title}</h3>
                    {body && (
                        <p style={{
                            margin: "8px 0 0", padding: 0,
                            fontSize: 13.5, color: "#64748B", lineHeight: 1.55,
                        }}>{body}</p>
                    )}
                </div>
                <div style={{
                    padding: "16px 26px 22px",
                    display: "flex", gap: 10, justifyContent: "flex-end",
                    background: "#fff",
                }}>
                    <button type="button"
                        onClick={onClose}
                        style={{
                            padding: "10px 20px",
                            borderRadius: 10,
                            border: "2px solid #E2E8F0",
                            background: "#fff",
                            color: "#475569",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 14, fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.2s",
                        }}>
                        {cancelLabel}
                    </button>
                    <button type="button"
                        onClick={handleConfirm}
                        autoFocus
                        style={{
                            padding: "10px 22px",
                            borderRadius: 10,
                            border: "none",
                            background: danger
                                ? "linear-gradient(135deg, #DC2626, #EF4444)"
                                : "linear-gradient(135deg, #E8520A, #FF7434)",
                            color: "#fff",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 14, fontWeight: 700,
                            cursor: "pointer",
                            boxShadow: danger
                                ? "0 4px 14px rgba(220, 38, 38, 0.3)"
                                : "0 4px 14px rgba(232, 82, 10, 0.3)",
                            transition: "all 0.2s",
                        }}>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// Dark navy gradient background - full-screen, fixed, animated. Sits at z=0
// with content on top. Three decorative orange glow circles + a faint dot
// grid overlay. Mirrors the dEpr stakeholder form bg exactly.
function Bg() {
    return (
        <div className="dspr-rf-bg" style={{
            position: "fixed", inset: 0, zIndex: 0,
            background: "linear-gradient(135deg, #0D1B2A 0%, #1A3A5C 40%, #0D2E1E 100%)",
            backgroundSize: "400% 400%",
            animation: "gradMove 12s ease infinite",
            overflow: "hidden",
        }}>
            {[
                { w: 500, h: 500, t: -100, l: -150, op: 0.06 },
                { w: 400, h: 400, b: -80,  r: -100, op: 0.05 },
                { w: 300, h: 300, t: 160,  r: 80,   op: 0.04 },
            ].map((c, i) => (
                <div key={i} style={{
                    position: "absolute",
                    width: c.w, height: c.h,
                    top: c.t, left: c.l, bottom: c.b, right: c.r,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, rgba(232,82,10,${c.op * 3}) 0%, transparent 70%)`,
                    opacity: c.op * 10,
                }} />
            ))}
            <div style={{
                position: "absolute", inset: 0,
                backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.015) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
            }} />
        </div>
    );
}

function BrandHeader({ halfLabel }) {
    // Centered "Dolluz Corp." mark with orange-dot signature. Cycle pill sits
    // directly underneath.
    return (
        <header className="dspr-rf-brand">
            <div className="spr-brand-name">
                Dolluz Corp<span className="spr-brand-dot">.</span>
            </div>
            <div className="spr-brand-tag">Performance self review</div>
            {halfLabel && (
                <div>
                    <div className="dspr-rf-cycle-pill">{halfLabel}</div>
                </div>
            )}
        </header>
    );
}

function Stepper({ state }) {
    // 3-step indicator matching dEpr - Verify Email → Enter OTP → Complete Review.
    // state values:
    //   'email'  → user typing their email (Verify Email ACTIVE)
    //   'otp'    → code sent, entering it (Enter OTP ACTIVE)
    //   'prereq' → OTP verified, on Before You Begin (Complete Review ACTIVE)
    //   'form'   → on the form page (Complete Review ACTIVE)
    //   'done'   → review submitted (all three DONE)
    const steps = [
        { id: 1, label: "Verify email" },
        { id: 2, label: "Enter OTP" },
        { id: 3, label: "Complete review" },
    ];
    const activeIdx =
        state === "email"  ? 0 :
        state === "otp"    ? 1 :
        state === "prereq" ? 2 :
        state === "form"   ? 2 :
        state === "done"   ? 3 : 0;
    return (
        <div className="dspr-rf-stepper">
            {steps.map((s, i) => {
                const cls = i < activeIdx ? "done" : i === activeIdx ? "active" : "";
                return (
                    <React.Fragment key={s.id}>
                        <div className={`dspr-rf-step ${cls}`}>
                            <span className="dspr-rf-step-num">
                                {i < activeIdx ? "✓" : s.id}
                            </span>
                            <span>{s.label}</span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`dspr-rf-step-line ${i < activeIdx ? "done" : ""}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// OTP MODAL
// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
// ALREADY-SUBMITTED SCREEN
// Rendered immediately after /check when status is Submitted/Approved. No
// OTP, no form access. Employee uses admin@dolluzcorp.com for any updates.
// ════════════════════════════════════════════════════════════════════════════
function AlreadySubmittedScreen({ reviewMeta }) {
    const halfLabel = reviewMeta?.cycle?.half_label || "";
    const submittedAt = reviewMeta?.submitted_at;
    const empName = reviewMeta?.employee
        ? `${reviewMeta.employee.first_name || ""} ${reviewMeta.employee.last_name || ""}`.trim()
        : "this employee";
    // Visually distinct from ThankYouPage on purpose - this screen is what
    // the user sees on RELOAD (or from another party's tab), not the
    // celebratory "you just submitted" moment. Slate/lock palette + neutral
    // wording. ThankYouPage stays the green-celebratory experience.
    return (
        <div className="dspr-rf-card dspr-rf-card-compact spr-fade-up">
            <div className="dspr-rf-thanks dspr-rf-already-card">
                {/* Same green check seal as ThankYouPage - both terminal
                    states are positive; the iconography matches by design. */}
                <div className="dspr-rf-thanks-seal dspr-rf-already-seal-green">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" className="dspr-rf-check-draw" />
                    </svg>
                </div>
                <h2 className="dspr-rf-thanks-title">Already submitted</h2>
                <p className="dspr-rf-thanks-lead">
                    Your Self Review{halfLabel ? <> for <strong>{halfLabel}</strong></> : null} has already been submitted.
                    <br />No further action is needed.
                </p>

                <div className="dspr-rf-thanks-stats">
                    <div className="dspr-rf-thanks-stat-row">
                        <span aria-hidden="true">🔒</span>
                        <span>This review link has expired</span>
                    </div>
                </div>

                <div className="dspr-rf-thanks-contact">
                    For any amendments or queries, please contact<br />
                    <a href="mailto:admin@dolluzcorp.com" className="dspr-rf-mail-link">admin@dolluzcorp.com</a>
                </div>

                <div className="dspr-rf-thanks-brand">
                    <div className="dspr-rf-thanks-brand-name">
                        Dolluz Corp<span style={{ color: "#E8520A" }}>.</span>
                    </div>
                    <div className="dspr-rf-thanks-brand-tag">PERFORMANCE SELF REVIEW</div>
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// NOT-STARTED SCREEN
// Shown when status is "Not Started" - HR hasn't fired the request email yet.
// Form isn't open. No OTP, no content.
// ════════════════════════════════════════════════════════════════════════════
function NotStartedScreen({ reviewMeta }) {
    const halfLabel = reviewMeta?.cycle?.half_label || "";
    return (
        <div className="dspr-rf-card dspr-rf-card-compact spr-fade-up">
            <div className="dspr-rf-thanks">
                <div className="dspr-rf-thanks-seal dspr-rf-pending-seal">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v6l3 2" />
                    </svg>
                </div>
                <h2 className="dspr-rf-thanks-title">Not yet initiated</h2>
                <p className="dspr-rf-thanks-lead">
                    Your {halfLabel} self-performance review hasn't been opened yet by People Operations.
                </p>
                <p className="dspr-rf-thanks-lead">
                    The form will become available once the review cycle is initiated and you receive the request email.
                </p>
                <div className="dspr-rf-thanks-contact">
                    If you believe this is an error, please contact<br />
                    <a href="mailto:admin@dolluzcorp.com" className="dspr-rf-mail-link">admin@dolluzcorp.com</a>
                </div>
                <div className="dspr-rf-thanks-brand">
                    <div className="dspr-rf-thanks-brand-name">
                        Dolluz Corp<span style={{ color: "#E8520A" }}>.</span>
                    </div>
                    <div className="dspr-rf-thanks-brand-tag">PERFORMANCE SELF REVIEW</div>
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// DRAFT-SAVED SCREEN
// Shown when the user hits "Save & Exit" mid-review. Their answers are
// persisted; they can return any time via the same review link in their
// email and continue from where they left off.
// ════════════════════════════════════════════════════════════════════════════
function DraftSavedScreen({ reviewMeta, onResume }) {
    const halfLabel = reviewMeta?.cycle?.half_label || "";
    return (
        <div className="dspr-rf-card dspr-rf-card-compact spr-fade-up">
            <div className="dspr-rf-thanks">
                <div className="dspr-rf-thanks-seal">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                    </svg>
                </div>
                <h2 className="dspr-rf-thanks-title">Your draft is saved</h2>
                <p className="dspr-rf-thanks-lead">
                    Your {halfLabel} self-performance review is safely saved as a draft. Pick up where you left off any time.
                </p>
                <div className="dspr-rf-already-info">
                    <div className="dspr-rf-draft-actions">
                        <button type="button"
                            className="dspr-rf-btn dspr-rf-btn-primary"
                            onClick={onResume}>
                            ← Resume editing
                        </button>
                    </div>
                </div>
                <div className="dspr-rf-thanks-contact">
                    You can safely close this tab - your progress is stored on our servers.
                </div>
                <div className="dspr-rf-thanks-brand">
                    <div className="dspr-rf-thanks-brand-name">
                        Dolluz Corp<span style={{ color: "#E8520A" }}>.</span>
                    </div>
                    <div className="dspr-rf-thanks-brand-tag">PERFORMANCE SELF REVIEW</div>
                </div>
            </div>
        </div>
    );
}

function OtpModal({ reviewId, reviewMeta, onAuthenticated, onOtpSent, onOtpReset, inline }) {
    const [stage, setStage] = useState("idle");
    const [email, setEmail] = useState("");
    // OTP is held as a 6-element char array so each box can render its own
    // value + filled-state. Joined to a string before posting to /verify-otp.
    const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
    const otp = otpDigits.join("");
    const boxRefs = useRef([null, null, null, null, null, null]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    // Masked email the BACKEND issued the OTP to. Shown in the "We sent a
    // code to …" sub-line of the inbox card.
    const [sentToMasked, setSentToMasked] = useState("");
    const [isAdminPath, setIsAdminPath] = useState(false);
    // 10-minute countdown - resets on resend, clears on stage flip back to
    // idle. Stored in SECONDS so the JetBrains Mono mm:ss formatting is
    // straightforward.
    const [countdown, setCountdown] = useState(0);
    useEffect(() => {
        if (stage !== "sent" || countdown <= 0) return;
        const id = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
        return () => clearInterval(id);
    }, [stage, countdown]);
    // When entering the sent stage, focus the first box automatically.
    useEffect(() => {
        if (stage === "sent") {
            setTimeout(() => boxRefs.current[0]?.focus(), 50);
        }
    }, [stage]);

    const fmtCountdown = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
    const countdownPct = countdown > 0 ? (600 - countdown) / 600 : 1;

    // ── OTP box keystroke handlers ─────────────────────────────────────────
    const setDigit = (idx, val) => {
        // Only accept single digit. Multi-char input (paste) handled separately.
        const ch = String(val || "").replace(/\D/g, "").slice(-1);
        setOtpDigits(prev => {
            const next = [...prev];
            next[idx] = ch;
            return next;
        });
        if (ch && idx < 5) {
            // Auto-advance after typing a digit
            setTimeout(() => boxRefs.current[idx + 1]?.focus(), 10);
        }
        setErr(null);
    };
    const handleBackspace = (idx, e) => {
        if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) {
            // Empty box + backspace → focus previous + clear it
            e.preventDefault();
            setOtpDigits(prev => {
                const next = [...prev];
                next[idx - 1] = "";
                return next;
            });
            setTimeout(() => boxRefs.current[idx - 1]?.focus(), 10);
        } else if (e.key === "Enter" && otp.length === 6) {
            verify();
        } else if (e.key === "ArrowLeft" && idx > 0) {
            e.preventDefault();
            boxRefs.current[idx - 1]?.focus();
        } else if (e.key === "ArrowRight" && idx < 5) {
            e.preventDefault();
            boxRefs.current[idx + 1]?.focus();
        }
    };
    const handlePaste = (idx, e) => {
        e.preventDefault();
        const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
        if (!pasted) return;
        setOtpDigits(prev => {
            const next = [...prev];
            for (let i = 0; i < pasted.length && (idx + i) < 6; i++) {
                next[idx + i] = pasted[i];
            }
            return next;
        });
        // Focus the next-empty box (or last if fully filled)
        const targetIdx = Math.min(5, idx + pasted.length);
        setTimeout(() => boxRefs.current[targetIdx]?.focus(), 10);
        setErr(null);
    };

    const emp = reviewMeta?.employee;
    const empName = emp ? `${emp.first_name || ""} ${emp.last_name || ""}`.trim() : "";
    const empSubtitle = emp
        ? [emp.job_position, emp.department, reviewMeta?.cycle?.half_label].filter(Boolean).join(" · ")
        : "";

    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const requestOtp = async () => {
        if (!isEmailValid) {
            setErr("Enter a valid email address");
            return;
        }
        setBusy(true); setErr(null);
        try {
            const res = await apiFetch("/api/dspr/review/request-otp", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reviewId, email: email.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't send code");
            setSentToMasked(data.email_masked || "");
            setIsAdminPath(!!data.is_admin_path);
            setOtpDigits(["", "", "", "", "", ""]);  // reset boxes on (re)send
            setCountdown(600);                          // 10 min
            setStage("sent");
            // Tell the parent the email step is complete so the Stepper
            // flips from "Verify Email" active → done + "Enter OTP" active.
            if (onOtpSent) onOtpSent();
        } catch (e) {
            setErr(e.message);
        } finally {
            setBusy(false);
        }
    };

    const verify = async () => {
        if (!/^\d{6}$/.test(otp)) {
            setErr("Enter all 6 digits");
            return;
        }
        setBusy(true); setErr(null);
        try {
            const res = await apiFetch("/api/dspr/review/verify-otp", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reviewId, email: email.trim(), otp }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Verification failed");
            onAuthenticated(data.token, data);
        } catch (e) {
            setErr(e.message);
            // Trigger shake animation by toggling the error class via state
            setOtpDigits(prev => [...prev]);  // force re-render so shake replays
        } finally {
            setBusy(false);
        }
    };

    // Full email - user just typed it on the previous screen, masking is
    // pointless and confusing. Only fall back to the server-masked value
    // if for some reason we lost the local email state.
    const displayEmail = (email && email.trim()) || sentToMasked || "your email";

    // No wrapper function - defining it inside the component would make
    // React remount the inner card on every keystroke (each render
    // creates a new Wrapper component identity), causing the slide-up
    // animation to replay. Inline ternary at the outermost element
    // keeps the same DOM node across re-renders.
    const wrapperClass = inline ? "dspr-rf-otp-inline" : "dspr-rf-otp-backdrop";

    return (
        <div className={wrapperClass}>
            <div className="dspr-rf-otp-modal">
                <div className="dspr-rf-otp-head">
                    <div className="dspr-rf-eyebrow">Verification</div>
                    <h2 className="dspr-rf-otp-title">
                        {stage === "idle" ? "Verify your email" : "Check your inbox"}
                    </h2>
                    <p className="dspr-rf-otp-blurb">
                        {stage === "idle"
                            ? "Enter your registered work email - We will send a 6-digit code."
                            : "Enter the code below to access your review."}
                    </p>
                </div>

                <div className="dspr-rf-otp-body">
                    {stage === "idle" ? (
                        <>
                            {/* "YOU ARE REVIEWING" identity card - only on email
                                entry, never on the code-entry stage. Lets the
                                employee/admin confirm they're about to file the
                                right person's review before typing their email. */}
                            {emp && (
                                <div className="dspr-rf-otp-emp-card">
                                    <div className="dspr-rf-otp-emp-label">YOU ARE SELF REVIEW</div>
                                    <div className="dspr-rf-otp-emp-name">{empName || "-"}</div>
                                    {empSubtitle && (
                                        <div className="dspr-rf-otp-emp-sub">{empSubtitle}</div>
                                    )}
                                    <div className="dspr-rf-otp-emp-pills">
                                        {emp.department && (
                                            <span className="dspr-rf-otp-emp-pill department">{emp.department}</span>
                                        )}
                                        {emp.id && (
                                            <span className="dspr-rf-otp-emp-pill empid">{emp.id}</span>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="dspr-rf-otp-email-row">
                                <div className="dspr-rf-otp-email-label">Enter your work email to continue</div>
                                <input
                                    type="email"
                                    className="dspr-rf-otp-admin-email"
                                    placeholder="yourname@dolluzcorp.com"
                                    value={email}
                                    autoComplete="email"
                                    autoFocus
                                    disabled={busy}
                                    onChange={e => setEmail(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") requestOtp(); }}
                                />
                                <div className="dspr-rf-otp-email-hint">
                                    Your domain will be validated against Dolluz Corp's authorized domain.
                                </div>
                            </div>
                            <button type="button"
                                className="dspr-rf-btn dspr-rf-btn-primary dspr-rf-btn-block"
                                onClick={requestOtp}
                                disabled={busy || !isEmailValid}>
                                {busy ? "Sending…" : "Continue  →"}
                            </button>
                            <div className="dspr-rf-otp-email-hint" style={{ textAlign: "center", marginTop: 12 }}>
                                Need help? Contact <a href="mailto:admin@dolluzcorp.com" className="dspr-rf-mail-link">admin@dolluzcorp.com</a>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Inbox-check card with mail icon */}
                            <div className="dspr-rf-otp-inbox-card">
                                <div className="dspr-rf-otp-inbox-icon" aria-hidden="true">📬</div>
                                <div className="dspr-rf-otp-inbox-h">Checking?</div>
                                <div className="dspr-rf-otp-inbox-sub">
                                    We sent a 6-digit code to<br />
                                    <strong>{displayEmail}</strong>
                                </div>
                            </div>

                            {/* Countdown ring (10 minutes) */}
                            <div className="dspr-rf-otp-countdown">
                                <div className="dspr-rf-otp-countdown-ring">
                                    <svg width="60" height="60" viewBox="0 0 60 60"
                                        style={{ transform: "rotate(-90deg)" }}>
                                        <circle cx="30" cy="30" r="22" fill="none"
                                            stroke="#F1F5F9" strokeWidth="4" />
                                        <circle cx="30" cy="30" r="22" fill="none"
                                            stroke={countdown < 60 ? "#EF4444" : "#E8520A"}
                                            strokeWidth="4"
                                            strokeDasharray="138"
                                            strokeDashoffset={138 * countdownPct}
                                            strokeLinecap="round"
                                            style={{ transition: "stroke-dashoffset 0.9s linear" }} />
                                    </svg>
                                    <div className={`dspr-rf-otp-countdown-text ${countdown < 60 ? "warn" : ""}`}>
                                        {fmtCountdown(countdown)}
                                    </div>
                                </div>
                                <div className="dspr-rf-otp-countdown-label">code expires</div>
                            </div>

                            {/* Six digit boxes */}
                            <div className={`dspr-rf-otp-boxes ${err ? "error" : ""}`}>
                                {otpDigits.map((v, i) => (
                                    <input key={i}
                                        ref={el => boxRefs.current[i] = el}
                                        className={`spr-otp-input ${v ? "filled" : ""} ${err ? "error" : ""}`}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete={i === 0 ? "one-time-code" : "off"}
                                        maxLength={1}
                                        value={v}
                                        disabled={busy}
                                        onChange={e => setDigit(i, e.target.value)}
                                        onKeyDown={e => handleBackspace(i, e)}
                                        onPaste={e => handlePaste(i, e)}
                                        onFocus={e => e.target.select()}
                                    />
                                ))}
                            </div>

                            <button type="button"
                                className="dspr-rf-btn dspr-rf-btn-primary dspr-rf-btn-block"
                                onClick={verify}
                                disabled={busy || otp.length < 6}>
                                {busy ? "Verifying…" : "Verify & Continue"}
                            </button>
                            <button type="button"
                                className="dspr-rf-btn-link dspr-rf-otp-resend"
                                onClick={requestOtp}
                                disabled={busy}>
                                {countdown === 0 ? "Resend code" : "Didn't get it? Resend"}
                            </button>
                            <button type="button"
                                className="dspr-rf-btn-link dspr-rf-otp-mode-toggle"
                                onClick={() => {
                                    if (busy) return;
                                    setStage("idle");
                                    setOtpDigits(["","","","","",""]);
                                    setErr(null);
                                    if (onOtpReset) onOtpReset();
                                }}
                                disabled={busy}>
                                ← Use a different email
                            </button>
                        </>
                    )}
                    {err && <div className="dspr-rf-otp-err">⚠ {err}</div>}
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// PAGE 1 - BEFORE YOU BEGIN
// ════════════════════════════════════════════════════════════════════════════
function PrereqPage({ cycle, disabled, onProceed }) {
    const [acked, setAcked] = useState(false);

    if (!cycle) {
        return <div className="dspr-rf-card"><div className="dspr-rf-loading">Loading cycle details…</div></div>;
    }

    const reportingPeriod = fmtDateRange(cycle.start_date, cycle.end_date);

    return (
        <div className="dspr-rf-card">
            <div className="dspr-rf-card-head">
                <div className="dspr-rf-eyebrow">Prerequisites</div>
                <h2 className="dspr-rf-card-h2">Before you begin</h2>
                <p className="dspr-rf-card-sub">
                    This Self Performance Review is for the period of <strong>{reportingPeriod}</strong>. Please read through, gather your inputs, then begin.
                </p>
            </div>
            <div className="dspr-rf-card-body">

                <div className="dspr-rf-block">
                    <h3>Why this self-review matters?</h3>
                    <p>It's how you demonstrate accountability for your contributions, reflect on what went well and where you want to grow, and align your view with your Home Manager's ahead of your 1:1. A strong self-review directly informs your feedback and rating outcome.</p>
                </div>

                <div className="dspr-rf-block">
                    <h3>How this works?</h3>
                    <ol className="dspr-rf-steps-num">
                        <li>Self-rate yourself across three areas - Team, Operational, and Customer/Client Excellence and back each rating with specifics.</li>
                        <li>Capture your top achievements, goals/KRO outcomes, and contributions beyond your role, with evidence where you have it.</li>
                        <li>Note your strengths, focus areas, and goals for the next half.</li>
                    </ol>
                </div>

                <div className="dspr-rf-block">
                    <h3>Timeline</h3>
                    <ul className="dspr-rf-bullets">
                        <li>Submit on or before <strong>{fmtDateShort(cycle.submission_deadline)}</strong>. Don't leave it to the last day - a missed or thin self-review affects your outcome.</li>
                        <li>Your Home Manager will hold the 1:1 discussion <strong>in FCFS basis</strong>. Be proactive in scheduling it.</li>
                    </ul>
                </div>

                <div className="dspr-rf-block">
                    <h3>Gather these before you start</h3>
                    <ul className="dspr-rf-bullets">
                        <li>Your goals / Key Results (KROs) and the outcomes &amp; metrics for each.</li>
                        <li>Delivery data - tasks scheduled vs delivered, cadence participation, quality metrics.</li>
                        <li>Appreciation emails or client/peer recognition (you can attach them).</li>
                        <li>Any documented peer or business-manager feedback.</li>
                    </ul>
                </div>

                <div className="dspr-rf-block">
                    <h3>Understanding the rating scale</h3>
                    <table className="dspr-rf-scale">
                        <thead><tr><th>Score</th><th>Meaning</th></tr></thead>
                        <tbody>
                            <tr><td className="dspr-rf-sc5">5</td><td>Consistently exceeded expectations</td></tr>
                            <tr><td>4</td><td>Exceeded expectations</td></tr>
                            <tr><td>3</td><td>Met expectations</td></tr>
                            <tr><td>2</td><td>Partially met expectations</td></tr>
                            <tr><td className="dspr-rf-sc1">1</td><td>Did not meet expectations</td></tr>
                            <tr><td>N/A</td><td>Not applicable to my role this cycle</td></tr>
                        </tbody>
                    </table>
                    <p className="dspr-rf-scale-note">Your ratings are a self-assessment; your Home Manager will review them and finalizes scoring post the 1:1 discussion.</p>
                </div>

                <div className="dspr-rf-block">
                    <h3>Declaration</h3>
                    <div className="dspr-rf-declaration">
                        By proceeding you confirm the information you provide is accurate and your own honest assessment, and you consent to it being used for your performance review process.
                    </div>
                </div>

                <div
                    className={`dspr-rf-ack ${acked ? "checked" : ""} ${disabled ? "is-disabled" : ""}`}
                    onClick={() => { if (!disabled) setAcked(v => !v); }}>
                    <div className="dspr-rf-ack-box">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                            <path d="M5 12l5 5L20 6" />
                        </svg>
                    </div>
                    <label>
                        I have read the above, gathered my inputs, and I am ready to begin.
                        <span>Required to continue.</span>
                    </label>
                </div>

                <div className="dspr-rf-nav">
                    <div />
                    <div className="dspr-rf-btns">
                        <button type="button"
                            className="dspr-rf-btn dspr-rf-btn-primary"
                            disabled={disabled || !acked}
                            onClick={onProceed}>
                            Begin Review →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// PAGE 2 - FORM
// ════════════════════════════════════════════════════════════════════════════
function FormPage({
    session, token,
    responses, setResponses,
    attachmentsByQ, setAttachmentsByQ,
    savedAt, setSavedAt,
    saving, setSaving,
    clientProject, setClientProject,
    clientProjectOther, setClientProjectOther,
    clientProjectOptions,
    onBack, onSubmitted, onSavedAndExit,
}) {
    const { cycle, employee, manager } = session;
    const [submitting, setSubmitting] = useState(false);
    // Inline banner - replaces Swal popups. { kind, message } or null.
    // Cleared by the user or auto-cleared after 4s for success messages.
    const [banner, setBanner] = useState(null);
    const showBanner = (kind, message) => {
        setBanner({ kind, message });
        if (kind === "success" || kind === "info") {
            setTimeout(() => setBanner(null), 4000);
        }
        // Scroll to top so the banner is visible
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* */ }
    };
    // Opt-in: employee requesting a PDF copy in their confirmation email.
    // Default unchecked - opt-in, not opt-out. Manager + admin/cc always get
    // the PDF regardless.
    const [wantsPdf, setWantsPdf] = useState(false);
    // One section at a time - Team / Operational / Customer / Growth.
    // Navigation via Previous / Next at the bottom + clickable tabs on top.
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const totalSections = DSPR_QUESTION_SCHEMA.sections.length;
    const currentSection = DSPR_QUESTION_SCHEMA.sections[currentSectionIndex];
    const isFirstSection = currentSectionIndex === 0;
    const isLastSection = currentSectionIndex === totalSections - 1;

    const responsesRef = useRef(responses);
    const clientProjectRef = useRef(clientProject);
    const clientProjectOtherRef = useRef(clientProjectOther);
    useEffect(() => { responsesRef.current = responses; }, [responses]);
    useEffect(() => { clientProjectRef.current = clientProject; }, [clientProject]);
    useEffect(() => { clientProjectOtherRef.current = clientProjectOther; }, [clientProjectOther]);

    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 60_000);
        return () => clearInterval(id);
    }, []);

    const saveTimerRef = useRef(null);
    const scheduleSave = () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => saveDraft(false), AUTO_SAVE_DEBOUNCE_MS);
    };
    const saveDraft = async (explicit) => {
        setSaving(true);
        try {
            // Persist custom text only when "Other" is the picked value.
            // Switching back to a standard option clears it on the next save.
            const cpValue = clientProjectRef.current || "";
            const cpCustom = cpValue === "Other"
                ? (clientProjectOtherRef.current || "").trim()
                : "";
            const payload = {
                ...responsesRef.current,
                __client_project: { value: cpValue, custom: cpCustom },
            };
            const res = await apiFetch("/api/dspr/review/draft", {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({ responses: payload }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Save failed");
            setSavedAt(data.saved_at || new Date().toISOString());
            if (explicit) {
                showBanner("success", "Draft saved.");
            }
        } catch (e) {
            if (explicit) showBanner("error", `Save failed: ${e.message}`);
        } finally {
            setSaving(false);
        }
    };
    useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

    const updateAnswer = (questionId, newAnswer) => {
        setResponses(prev => ({ ...prev, [questionId]: newAnswer }));
        scheduleSave();
    };
    const onClientProjectChange = (value) => {
        setClientProject(value);
        scheduleSave();
    };
    // ── File upload + delete handlers - wire UploadWidget to backend ────────
    const uploadAttachment = async (questionId, file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("question_id", questionId);
        const res = await apiFetch("/api/dspr/review/upload", {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },  // browser sets multipart Content-Type
            body: formData,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Upload failed");
        setAttachmentsByQ(prev => ({
            ...prev,
            [questionId]: [...(prev[questionId] || []), data],
        }));
    };
    const deleteAttachment = async (questionId, attachmentId) => {
        const res = await apiFetch(`/api/dspr/review/upload/${attachmentId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Delete failed");
        setAttachmentsByQ(prev => ({
            ...prev,
            [questionId]: (prev[questionId] || []).filter(a => a.id !== attachmentId),
        }));
    };

    // ── Submit handler - POST /submit → ThankYou ────────────────────────────
    const handleSubmit = async () => {
        // Client / Project is required - block submit if not selected.
        if (!clientProject) {
            showBanner("warning", "Please pick your Client / Project from the dropdown at the top of the form.");
            return;
        }
        // Validate "Other" → custom text is required + sized
        if (clientProject === "Other") {
            const trimmed = (clientProjectOther || "").trim();
            if (trimmed.length < 2) {
                showBanner("warning", "You picked 'Other' for Client / Project - please type the actual project name (at least 2 characters).");
                return;
            }
            if (trimmed.length > 50) {
                showBanner("warning", "Please keep the Client / Project name under 50 characters.");
                return;
            }
        }

        // Flush any pending debounced save first so the submit sees the
        // very latest draft state.
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        try { await saveDraft(false); } catch { /* tolerated */ }

        setSubmitting(true);
        try {
            // Send client_project DIRECTLY in the submit body so the backend
            // doesn't have to depend on the most recent /draft having
            // committed. Belt + suspenders: server still persists the row.
            const cpValueNow  = clientProjectRef.current || "";
            const cpCustomNow = cpValueNow === "Other"
                ? (clientProjectOtherRef.current || "").trim()
                : "";
            const res = await apiFetch("/api/dspr/review/submit", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                    wants_pdf: wantsPdf,
                    client_project: { value: cpValueNow, custom: cpCustomNow },
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                if (data.missing_question_ids?.length) {
                    showBanner("error", `Some required questions are not answered: ${data.missing_question_ids.map(id => id.toUpperCase()).join(", ")}`);
                    return;
                }
                throw new Error(data.error || "Submit failed");
            }
            onSubmitted(data);
        } catch (e) {
            showBanner("error", `Submit failed: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const reportingPeriod = fmtDateRange(cycle.start_date, cycle.end_date);

    // Top-of-form dark progress card - replaces the white eyebrow/title/sub
    // block. Shows brand mark on the left, cycle pill + percent on the right,
    // and an orange-gradient progress bar with counters below.
    const totalQs = DSPR_QUESTION_SCHEMA.sections.flatMap(s => s.questions).length;
    const answeredQs = DSPR_QUESTION_SCHEMA.sections.flatMap(s => s.questions)
        .filter(q => isAnswered(q, responses[q.id])).length;
    const pct = totalQs > 0 ? Math.round((answeredQs / totalQs) * 100) : 0;

    return (
        <>
        {/* Inline status banner - surfaces errors, warnings, and confirmations
            instead of Swal popups (Swal would clash visually with the dark
            navy + white-card theme). */}
        <Banner kind={banner?.kind} message={banner?.message}
                onDismiss={() => setBanner(null)} />
        <div className="dspr-rf-progress-card spr-fade-up">
            <div className="dspr-rf-progress-top">
                <div className="dspr-rf-progress-brand">
                    <span className="dspr-rf-progress-brand-name">
                        Dolluz Corp<span style={{ color: "#E8520A" }}>.</span>
                    </span>
                    <span className="dspr-rf-progress-brand-tag">Performance self review</span>
                </div>
                <div className="dspr-rf-progress-meta">
                    <span className="dspr-rf-progress-cycle">{cycle.half_label || "-"}</span>
                    <span className="dspr-rf-progress-pct">{pct}%</span>
                </div>
            </div>
            <div className="dspr-rf-progress-bar">
                <div className="dspr-rf-progress-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="dspr-rf-progress-counters">
                <span><strong>{answeredQs}</strong> of <strong>{totalQs}</strong> criteria answered</span>
                <span>{Math.max(0, totalQs - answeredQs)} remaining</span>
            </div>
        </div>

        {/* Dark employee info card - gradient navy bg, orange avatar */}
        <div className="dspr-rf-identity dspr-rf-identity-dark spr-fade-up">
                    <div className="dspr-rf-identity-top">
                        <div className="dspr-rf-identity-avatar">
                            {`${(employee.first_name || "?")[0] || ""}${(employee.last_name || "")[0] || ""}`.toUpperCase()}
                        </div>
                        <div>
                            <div className="dspr-rf-identity-name">
                                {`${employee.first_name || ""} ${employee.last_name || ""}`.trim() || "-"}
                            </div>
                            <div className="dspr-rf-identity-sub">
                                {[employee.job_position, employee.id].filter(Boolean).join(" · ")}
                            </div>
                        </div>
                    </div>
                    <div className="dspr-rf-identity-grid">
                        <div className="dspr-rf-identity-cell">
                            <div className="dspr-rf-identity-k">DEPARTMENT</div>
                            <div className="dspr-rf-identity-v">{employee.department || "-"}</div>
                        </div>
                        <div className="dspr-rf-identity-cell">
                            <div className="dspr-rf-identity-k">CYCLE</div>
                            <div className="dspr-rf-identity-v">{cycle.half_label || "-"}</div>
                        </div>
                        <div className="dspr-rf-identity-cell">
                            <div className="dspr-rf-identity-k">HOME MANAGER</div>
                            <div className="dspr-rf-identity-v">
                                {manager ? `${manager.first_name} ${manager.last_name}` : "-"}
                            </div>
                        </div>
                        <div className="dspr-rf-identity-cell">
                            <div className="dspr-rf-identity-k">
                                CLIENT / PROJECT <span className="dspr-rf-required" aria-hidden="true">*</span>
                            </div>
                            <select
                                className="dspr-rf-identity-select"
                                value={clientProject}
                                onChange={e => onClientProjectChange(e.target.value)}>
                                <option value="">Select…</option>
                                {clientProjectOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                            {clientProject === "Other" && (() => {
                                // Visible character counter - gives the user a
                                // concrete sense of the 50-char ceiling. Turns
                                // amber past 38, red past 46, full-red at 50.
                                const len = (clientProjectOther || "").length;
                                const tone =
                                    len >= 50 ? "#DC2626" :
                                    len >= 46 ? "#DC2626" :
                                    len >= 38 ? "#B45309" :
                                                "rgba(255, 255, 255, 0.5)";
                                return (
                                    <>
                                        <input
                                            type="text"
                                            className="dspr-rf-id-other-input"
                                            placeholder="Type your project name…"
                                            value={clientProjectOther}
                                            maxLength={50}
                                            onChange={e => {
                                                setClientProjectOther(e.target.value);
                                                scheduleSave();
                                            }}
                                        />
                                        <div style={{
                                            fontSize: 10.5,
                                            fontWeight: 600,
                                            color: tone,
                                            textAlign: "right",
                                            marginTop: 4,
                                            letterSpacing: 0.3,
                                            fontFamily: "'JetBrains Mono', monospace",
                                        }}>
                                            {len} / 50
                                            {len >= 50 && " · max reached"}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                {/* Section navigation tabs - click to jump, current highlighted */}
                <div className="dspr-rf-section-tabs">
                    {DSPR_QUESTION_SCHEMA.sections.map((section, idx) => {
                        const answeredCount = section.questions.filter(q => isAnswered(q, responses[q.id])).length;
                        const total = section.questions.length;
                        const isComplete = answeredCount === total;
                        const isActive = idx === currentSectionIndex;
                        return (
                            <button key={section.id} type="button"
                                className={`dspr-rf-section-tab dspr-rf-section-tab-${section.color} ${isActive ? "active" : ""} ${isComplete ? "complete" : ""}`}
                                onClick={() => {
                                    setCurrentSectionIndex(idx);
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}>
                                <span className="dspr-rf-section-tab-num">{idx + 1}</span>
                                <span className="dspr-rf-section-tab-name">{section.title}</span>
                                <span className="dspr-rf-section-tab-count">{answeredCount} / {total}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Only the current section's questions render - one at a time. */}
                <SectionCard
                    key={currentSection.id}
                    section={currentSection}
                    responses={responses}
                    attachmentsByQ={attachmentsByQ}
                    onUpdate={updateAnswer}
                    onUploadAttachment={uploadAttachment}
                    onDeleteAttachment={deleteAttachment}
                />

                <FormFooter
                    saving={saving} savedAt={savedAt}
                    responses={responses}
                    currentSectionIndex={currentSectionIndex}
                    totalSections={totalSections}
                    isFirstSection={isFirstSection}
                    isLastSection={isLastSection}
                    onPrev={() => {
                        setCurrentSectionIndex(i => Math.max(0, i - 1));
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onNext={() => {
                        setCurrentSectionIndex(i => Math.min(totalSections - 1, i + 1));
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onSaveExit={async () => {
                        if (saveTimerRef.current) {
                            clearTimeout(saveTimerRef.current);
                            saveTimerRef.current = null;
                        }
                        try {
                            await saveDraft(false);
                            onSavedAndExit();
                        } catch (e) {
                            showBanner("error", `Save failed: ${e.message}`);
                        }
                    }}
                    onSubmit={handleSubmit}
                    submitting={submitting}
                    wantsPdf={wantsPdf}
                    setWantsPdf={setWantsPdf} />

        {/* Corporate footer - sits OUTSIDE the white card, on the page
            background, matching the dEpr "Secured by …" pattern. */}
        <div className="dspr-rf-page-footer">
            Secured by Dolluz Corp · SPR Portal · {new Date().getFullYear()} · Confidential
        </div>
        </>
    );
}

// Inline-rendered footer so the counters refresh on every keystroke
function FormFooter({
    saving, savedAt, responses,
    currentSectionIndex, totalSections, isFirstSection, isLastSection,
    onPrev, onNext, onSaveExit, onSubmit,
    submitting, wantsPdf, setWantsPdf,
}) {
    const allQs = DSPR_QUESTION_SCHEMA.sections.flatMap(s => s.questions);
    const total = allQs.length;
    const answered = allQs.filter(q => isAnswered(q, responses[q.id])).length;
    const requiredPending = allQs.filter(q => q.required && !isAnswered(q, responses[q.id])).length;
    const canSubmit = requiredPending === 0;
    const submitDisabled = !canSubmit || submitting || saving;
    const submitTitle = requiredPending > 0
        ? `${requiredPending} required question(s) still need answers`
        : submitting ? "Submitting…"
        : "Submit your review";

    return (
        <>
            {/* TOP nav - right under the questions. ONLY Previous / Next /
                Save & Exit. Submit lives at the very bottom alongside the
                fine-print and the corporate footer. */}
            <div className="dspr-rf-nav">
                <div className="dspr-rf-nav-arrows">
                    <button type="button"
                        className="dspr-rf-btn dspr-rf-btn-ghost"
                        onClick={onPrev}
                        disabled={isFirstSection || saving || submitting}>
                        ← Previous
                    </button>
                    <button type="button"
                        className="dspr-rf-btn dspr-rf-btn-next"
                        onClick={onNext}
                        disabled={isLastSection || saving || submitting}>
                        Next →
                    </button>
                </div>
                <div className="dspr-rf-progress-count dspr-rf-progress-count-mid">
                    Section <strong>{currentSectionIndex + 1}</strong> of <strong>{totalSections}</strong>
                    <span className="dspr-rf-progress-sep">·</span>
                    <strong>{answered}</strong> of <strong>{total}</strong> answered
                </div>
                <div className="dspr-rf-btns">
                    <button type="button"
                        className="dspr-rf-btn dspr-rf-btn-ghost"
                        onClick={onSaveExit}
                        disabled={saving || submitting}>
                        💾 Save & Exit
                    </button>
                </div>
            </div>

            {/* PDF opt-in + Review Completeness + Submit cluster only appears
                AFTER the user has answered at least 4 questions (combined
                across any sections, not per-section). Avoids surfacing the
                submission UI for someone who just typed in one box. */}
            {answered >= 4 && (
                <div className="dspr-rf-submit-card spr-fade-up">
                    <div className="dspr-rf-pdf-optin">
                        <label className={`dspr-rf-pdf-optin-row ${wantsPdf ? "checked" : ""}`}>
                            <input
                                type="checkbox"
                                checked={wantsPdf}
                                onChange={e => setWantsPdf(e.target.checked)}
                                disabled={submitting}
                            />
                            <div className="dspr-rf-pdf-optin-text">
                                <div className="dspr-rf-pdf-optin-label">
                                    <span className="dspr-rf-pdf-optin-icon" aria-hidden="true">📧</span>
                                    Send a copy of this response to my email
                                </div>
                                <div className="dspr-rf-pdf-optin-sub">
                                    A PDF copy of your completed review will be sent to your authenticated email. A copy will also be sent to the reporting manager.
                                </div>
                            </div>
                        </label>
                    </div>

                    <div className="dspr-rf-completeness">
                        <div className="dspr-rf-completeness-title">Review completeness</div>
                        <div className="dspr-rf-completeness-row">
                            <span>Required questions</span>
                            <span className={canSubmit ? "dspr-rf-completeness-ok" : "dspr-rf-completeness-warn"}>
                                {canSubmit ? "All answered ✓" : `${requiredPending} pending`}
                            </span>
                        </div>
                        {DSPR_QUESTION_SCHEMA.sections.map(section => {
                            const sTotal = section.questions.length;
                            const sAnswered = section.questions.filter(q => isAnswered(q, responses[q.id])).length;
                            const done = sAnswered === sTotal;
                            return (
                                <div key={section.id} className="dspr-rf-completeness-row">
                                    <span>{section.title}</span>
                                    <span className={done ? "dspr-rf-completeness-ok" : ""}>
                                        {sAnswered} / {sTotal}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Big full-width Submit button - disabled with explanatory
                        label until all required answered. Matches the dEpr
                        "Rate X more criteria to submit" pattern. */}
                    <button type="button"
                        className="dspr-rf-btn dspr-rf-btn-primary dspr-rf-submit-big"
                        disabled={submitDisabled}
                        title={submitTitle}
                        onClick={onSubmit}>
                        {submitting
                            ? "Submitting…"
                            : canSubmit
                                ? "Submit Self Review"
                                : `Answer ${requiredPending} more to submit`}
                    </button>

                    {/* Fine-print below Submit */}
                    <div className="dspr-rf-submit-fineprint">
                        Once submitted, this review link will expire permanently.
                        <br />
                        For amendments, contact <a href="mailto:admin@dolluzcorp.com" className="dspr-rf-mail-link">admin@dolluzcorp.com</a>
                    </div>
                </div>
            )}
        </>
    );
}
function IdField({ label, auto, children }) {
    return (
        <div className={`dspr-rf-id-f ${auto ? "auto" : ""}`}>
            <div className="dspr-rf-id-k">{label}</div>
            <div className="dspr-rf-id-v">{children || "-"}</div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// SECTIONS + QUESTIONS
// ════════════════════════════════════════════════════════════════════════════
// Sections render fully expanded inline - matches dEpr's scroll-once layout.
// Header is a static visual separator (colored dot + title + subtitle +
// answered-count pill). No accordion, no click-to-toggle, no chevron.
function SectionCard({ section, responses, attachmentsByQ, onUpdate, onUploadAttachment, onDeleteAttachment, readOnly, downloadAttachment }) {
    const total = section.questions.length;
    const answeredCount = section.questions.filter(q => isAnswered(q, responses[q.id])).length;
    const complete = answeredCount === total;
    return (
        <div className={`dspr-rf-sec dspr-rf-sec-${section.color}`}>
            <div className="dspr-rf-sec-head">
                <span className="dspr-rf-sec-dot" />
                <span className="dspr-rf-sec-lbl">{section.title}</span>
                <span className="dspr-rf-sec-meta">{section.subtitle}</span>
                <span className={`dspr-rf-sec-count ${complete ? "complete" : ""}`}>
                    {answeredCount} / {total}
                </span>
            </div>
            <div className="dspr-rf-sec-body">
                {section.questions.map(q => (
                    <QuestionRenderer
                        key={q.id}
                        question={q}
                        value={responses[q.id]}
                        attachments={attachmentsByQ?.[q.id] || []}
                        onChange={(v) => onUpdate(q.id, v)}
                        onUploadAttachment={onUploadAttachment}
                        onDeleteAttachment={onDeleteAttachment}
                        downloadAttachment={downloadAttachment}
                        readOnly={readOnly}
                    />
                ))}
            </div>
        </div>
    );
}

// Per-type display tags (match the mockup's <span class="tag"> chips)
const TYPE_TAGS = {
    text:                              [{ label: "Text" }],
    single_choice:                     [{ label: "Single choice" }],
    single_choice_with_text:           [{ label: "Single choice + text" }],
    single_choice_with_text_and_upload:[{ label: "Single choice + text" }, { label: "+ upload", up: true }],
    text_with_upload:                  [{ label: "Text" }, { label: "+ upload", up: true }],
    rating_1_to_5_with_text:           [{ label: "Rating 1-5 + text" }],
    number_pair_with_text:             [{ label: "Number + text" }],
};

function QShell({ question, answered, children }) {
    const isOptional = question.required === false;
    const showNotAnswered = !answered && !isOptional;
    return (
        <div className={`dspr-rf-q ${showNotAnswered ? "dspr-rf-q-unanswered" : ""}`}>
            <div className="dspr-rf-q-head">
                <span className="dspr-rf-q-num">Q{question.number}</span>
                <div className="dspr-rf-q-title">
                    {question.title}
                    {isOptional && <span className="dspr-rf-q-optional-pill">Optional</span>}
                    {showNotAnswered && (
                        <span className="dspr-rf-q-not-answered-pill">● Not answered</span>
                    )}
                </div>
            </div>
            {question.description && (
                <div className="dspr-rf-q-desc">{question.description}</div>
            )}
            <div className="dspr-rf-field">{children}</div>
        </div>
    );
}

function QuestionRenderer({ question, value, attachments, onChange, onUploadAttachment, onDeleteAttachment, downloadAttachment, readOnly }) {
    const props = { question, value, onChange, readOnly };
    const uploadProps = { attachments, onUploadAttachment, onDeleteAttachment, downloadAttachment };
    switch (question.type) {
        case "text":                                return <TextQ {...props} />;
        case "single_choice":                       return <SingleChoiceQ {...props} />;
        case "single_choice_with_text":             return <SingleChoiceTextQ {...props} />;
        case "single_choice_with_text_and_upload":  return <SingleChoiceTextQ {...props} {...uploadProps} withUpload />;
        case "text_with_upload":                    return <TextQ {...props} {...uploadProps} withUpload />;
        case "rating_1_to_5_with_text":             return <RatingTextQ {...props} />;
        case "number_pair_with_text":               return <NumberPairTextQ {...props} />;
        default:
            return <div>Unknown question type: {question.type}</div>;
    }
}

// Bytes pretty-print
function fmtBytes(b) {
    if (b == null) return "-";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

const MAX_ATTACHMENTS_PER_Q = 5;
// 5 MB total per question - combined size of all attachments. One 5MB
// file or five 1MB files, but not both. Keep in sync with the backend's
// MAX_FILE_SIZE constant in dSpr_ReviewForm_server.js. Client-side check
// is a friendly preflight; the backend enforces this authoritatively.
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
// Extensions first, MIME types second. The MIME list intentionally includes the
// zip aliases different browsers emit (Chrome/Windows says
// application/x-zip-compressed) so the OS file picker doesn't grey out a valid
// .zip or .xlsx. Backend enforces the real allowlist per-extension - see
// ALLOWED_TYPES in dSpr_ReviewForm_server.js.
const ACCEPT_FILE_TYPES = [
    ".pdf", ".png", ".jpg", ".jpeg", ".eml", ".zip", ".xlsx", ".xls",
    "application/pdf",
    "image/png",
    "image/jpeg",
    "message/rfc822",
    "application/zip",
    "application/x-zip-compressed",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
].join(",");

// Fully functional upload widget - drag/drop + click-to-browse + remove.
// readOnly mode hides the dropzone and shows attachments as download links.
function UploadWidget({ questionId, label, attachments, onUploadAttachment, onDeleteAttachment, downloadAttachment, readOnly }) {
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);
    const list = attachments || [];
    const atCap = list.length >= MAX_ATTACHMENTS_PER_Q;
    // Per-question inline banner - no Swal in the review form.
    const [uploadBanner, setUploadBanner] = useState(null);
    // Themed confirm dialog state - replaces window.confirm for attachment
    // removal. { title, body, confirmLabel, danger, onConfirm } or null.
    const [confirmState, setConfirmState] = useState(null);
    const showUploadBanner = (kind, message) => {
        setUploadBanner({ kind, message });
        if (kind === "success" || kind === "info") {
            setTimeout(() => setUploadBanner(null), 4000);
        }
    };

    const handleFiles = async (files) => {
        const arr = Array.from(files || []);
        if (!arr.length) return;
        setUploadBanner(null);
        setUploading(true);
        try {
            for (const file of arr) {
                if (list.length + 1 > MAX_ATTACHMENTS_PER_Q) {
                    showUploadBanner("warning", `Limit reached - max ${MAX_ATTACHMENTS_PER_Q} attachments per question.`);
                    break;
                }
                // Client-side size guard - server enforces this too. The
                // 5MB cap is the *combined* total per question (so one 5MB
                // file, or five 1MB files - but not both). Sum the existing
                // attachments + the new file; reject if that crosses 5MB.
                const existingBytes = list.reduce((sum, a) => sum + (a.size_bytes || 0), 0);
                if (existingBytes + file.size > MAX_FILE_SIZE_BYTES) {
                    const remaining = Math.max(0, MAX_FILE_SIZE_BYTES - existingBytes);
                    const remainingMB = (remaining / (1024 * 1024)).toFixed(1);
                    showUploadBanner("error",
                        existingBytes === 0
                            ? "File exceeds 5MB limit. Please use a smaller file."
                            : `Total attachments for this question would exceed 5MB. You have ${remainingMB} MB remaining.`);
                    break;
                }
                // Duplicate guard - same filename + same byte size in the
                // existing attachment list means the user already uploaded
                // this file. Cheap signal; avoids accidental double-clicks
                // or re-drops without blocking legitimate same-name files
                // with different sizes.
                const isDup = list.some(a =>
                    a.original_name === file.name && a.size_bytes === file.size
                );
                if (isDup) {
                    showUploadBanner("warning", `"${file.name}" is already attached to this question.`);
                    break;
                }
                try {
                    await onUploadAttachment(questionId, file);
                } catch (e) {
                    showUploadBanner("error", `Upload failed: ${e.message}`);
                    break;
                }
            }
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (readOnly || atCap) return;
        handleFiles(e.dataTransfer.files);
    };

    const handleRemove = (id) => {
        // Themed ConfirmDialog - matches the navy/orange form theme. No
        // native window.confirm (looks unstyled) and no Swal (clashes with
        // the dark theme).
        const target = list.find(a => a.id === id);
        setConfirmState({
            title: "Remove this attachment?",
            body: target ? `"${target.original_name}" will be deleted from your review. You can re-upload it any time.` : undefined,
            confirmLabel: "Remove",
            cancelLabel: "Cancel",
            danger: true,
            onConfirm: async () => {
                try {
                    await onDeleteAttachment(questionId, id);
                } catch (e) {
                    showUploadBanner("error", `Couldn't remove: ${e.message}`);
                }
            },
        });
    };

    return (
        <div className="dspr-rf-upload-wrap">
            {/* Themed confirm dialog - used for attachment removal */}
            <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
            {/* Inline banner - file size / limit / upload / delete errors */}
            <Banner kind={uploadBanner?.kind} message={uploadBanner?.message}
                    onDismiss={() => setUploadBanner(null)} />
            {!readOnly && (
                <>
                <div className={`dspr-rf-upload ${dragActive ? "dragover" : ""} ${atCap ? "is-disabled" : ""}`}
                    onClick={() => !atCap && fileInputRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); if (!atCap) setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 16V4M5 11l7-7 7 7M4 20h16" />
                    </svg>
                    {uploading ? "Uploading…"
                        : atCap ? `Max ${MAX_ATTACHMENTS_PER_Q} files reached - remove one to add more`
                        : (label || "Drag a file here or click to browse")}
                    <input ref={fileInputRef}
                        type="file"
                        style={{ display: "none" }}
                        accept={ACCEPT_FILE_TYPES}
                        onChange={e => handleFiles(e.target.files)}
                        disabled={atCap}
                    />
                </div>
                {!atCap && (
                    <div className="dspr-rf-upload-hint" style={{
                        fontSize: 11, color: "#94A3B8", marginTop: 6, textAlign: "center",
                    }}>
                        PDF, PNG, JPG, XLSX, XLS, EML, ZIP · 5MB max total · up to {MAX_ATTACHMENTS_PER_Q} files per question
                    </div>
                )}
                </>
            )}
            {list.length > 0 && (
                <div className="dspr-rf-attachment-list">
                    {list.map(a => (
                        <div key={a.id} className="dspr-rf-attachment-row">
                            <span className="dspr-rf-attachment-icon">📎</span>
                            {readOnly && downloadAttachment ? (
                                <button type="button"
                                    className="dspr-rf-attachment-name dspr-rf-attachment-link"
                                    onClick={() => downloadAttachment(a.id, a.original_name)}>
                                    {a.original_name}
                                </button>
                            ) : (
                                <span className="dspr-rf-attachment-name">{a.original_name}</span>
                            )}
                            <span className="dspr-rf-attachment-size">{fmtBytes(a.size_bytes)}</span>
                            {!readOnly && (
                                <button type="button"
                                    className="dspr-rf-attachment-remove"
                                    onClick={() => handleRemove(a.id)}
                                    title="Remove attachment">×</button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TextQ({ question, value, onChange, readOnly, withUpload, attachments, onUploadAttachment, onDeleteAttachment, downloadAttachment }) {
    const v = value?.value ?? "";
    return (
        <QShell question={question} answered={isAnswered(question, value)}>
            <textarea
                placeholder={question.placeholder || ""}
                value={v}
                readOnly={readOnly}
                rows={4}
                onChange={e => onChange({ value: e.target.value })}
            />
            {withUpload && (
                <UploadWidget
                    questionId={question.id}
                    label={question.uploadLabel || "Upload supporting files (optional)"}
                    attachments={attachments}
                    onUploadAttachment={onUploadAttachment}
                    onDeleteAttachment={onDeleteAttachment}
                    downloadAttachment={downloadAttachment}
                    readOnly={readOnly}
                />
            )}
        </QShell>
    );
}

function SingleChoiceQ({ question, value, onChange, readOnly }) {
    const choice = value?.choice;
    return (
        <QShell question={question} answered={isAnswered(question, value)}>
            <div className="dspr-rf-opts">
                {question.options.map((opt, i) => (
                    <label key={i} className="dspr-rf-opt">
                        <input type="radio" name={question.id}
                            checked={choice === i} disabled={readOnly}
                            onChange={() => onChange({ choice: i })} />
                        {opt}
                    </label>
                ))}
            </div>
        </QShell>
    );
}

function SingleChoiceTextQ({ question, value, onChange, readOnly, withUpload, attachments, onUploadAttachment, onDeleteAttachment, downloadAttachment }) {
    const choice = value?.choice;
    const text = value?.text ?? "";
    return (
        <QShell question={question} answered={isAnswered(question, value)}>
            <div className="dspr-rf-opts">
                {question.options.map((opt, i) => (
                    <label key={i} className="dspr-rf-opt">
                        <input type="radio" name={question.id}
                            checked={choice === i} disabled={readOnly}
                            onChange={() => onChange({ choice: i, text })} />
                        {opt}
                    </label>
                ))}
            </div>
            <textarea
                placeholder={question.textPlaceholder || ""}
                value={text}
                readOnly={readOnly}
                rows={3}
                onChange={e => onChange({ choice, text: e.target.value })}
            />
            {withUpload && (
                <UploadWidget
                    questionId={question.id}
                    label={question.uploadLabel || "Upload supporting files (optional)"}
                    attachments={attachments}
                    onUploadAttachment={onUploadAttachment}
                    onDeleteAttachment={onDeleteAttachment}
                    downloadAttachment={downloadAttachment}
                    readOnly={readOnly}
                />
            )}
        </QShell>
    );
}

function RatingTextQ({ question, value, onChange, readOnly }) {
    const rating = value?.rating;
    const text = value?.text ?? "";
    return (
        <QShell question={question} answered={isAnswered(question, value)}>
            <div className="dspr-rf-scale5">
                {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button"
                        className={rating === n ? "on" : ""}
                        disabled={readOnly}
                        onClick={() => onChange({ rating: n, text })}>
                        {n}
                    </button>
                ))}
            </div>
            <div className="dspr-rf-scale-cap">
                <span>{question.scaleLowLabel || "Low"}</span>
                <span>{question.scaleHighLabel || "High"}</span>
            </div>
            <textarea
                placeholder={question.textPlaceholder || ""}
                value={text}
                readOnly={readOnly}
                rows={3}
                onChange={e => onChange({ rating, text: e.target.value })}
            />
        </QShell>
    );
}

function NumberPairTextQ({ question, value, onChange, readOnly }) {
    const num1 = value?.num1 ?? "";
    const num2 = value?.num2 ?? "";
    const text = value?.text ?? "";
    const update = (next) => onChange({ num1, num2, text, ...next });

    // Sane upper bound - H1/H2 task counts realistically don't exceed a few
    // hundred. 99_999 catches typos without artificially limiting heavy
    // workloads. Anything beyond gets clamped silently in the change handler.
    const MAX_NUM = 99999;
    // `type="text" inputMode="numeric"` keeps the mobile numeric keypad
    // but stops the browser from accepting `e`, `+`, `-`, decimals, and
    // scrollwheel mutations that the native number input allows.
    const sanitize = (raw) => {
        const digitsOnly = String(raw || "").replace(/[^\d]/g, "");
        if (!digitsOnly) return "";
        const n = Number(digitsOnly);
        return Number.isFinite(n) ? Math.min(n, MAX_NUM) : "";
    };
    return (
        <QShell question={question} answered={isAnswered(question, value)}>
            <div className="dspr-rf-twocol">
                <input className="dspr-rf-num" type="text"
                    inputMode="numeric" pattern="[0-9]*"
                    maxLength={5}
                    placeholder={question.input1Placeholder || "Number 1"}
                    value={num1} readOnly={readOnly}
                    onChange={e => update({ num1: sanitize(e.target.value) })}
                    onKeyDown={e => {
                        // Block the only character that maxLength + pattern
                        // don't catch: the typed minus / plus / dot / e.
                        if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                    }} />
                <input className="dspr-rf-num" type="text"
                    inputMode="numeric" pattern="[0-9]*"
                    maxLength={5}
                    placeholder={question.input2Placeholder || "Number 2"}
                    value={num2} readOnly={readOnly}
                    onChange={e => update({ num2: sanitize(e.target.value) })}
                    onKeyDown={e => {
                        if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                    }} />
            </div>
            <textarea
                placeholder={question.textPlaceholder || ""}
                value={text}
                readOnly={readOnly}
                rows={3}
                onChange={e => update({ text: e.target.value })}
            />
        </QShell>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// READ-ONLY VIEW
// ════════════════════════════════════════════════════════════════════════════
function ReadOnlyView({ session, responses, attachmentsByQ, clientProject, clientProjectOther, token }) {
    // Display logic: if user picked "Other" + typed custom text, show the
    // custom text only (it's the real project name). Otherwise show the
    // dropdown pick directly.
    const clientProjectDisplay = clientProject === "Other" && clientProjectOther
        ? clientProjectOther
        : (clientProject || "-");
    const { cycle, employee, manager } = session;
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    // Inline banner - replaces Swal popups for the download flows.
    const [banner, setBanner] = useState(null);
    const showBanner = (kind, message) => {
        setBanner({ kind, message });
        if (kind === "success" || kind === "info") {
            setTimeout(() => setBanner(null), 4000);
        }
    };

    // PDF re-download via authenticated request - backend streams the same
    // generator output as the manager-notification email attachment.
    const downloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const res = await apiFetch("/api/dspr/review/pdf", {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                let msg = "Couldn't download PDF";
                try { const j = await res.json(); msg = j.error || msg; } catch { /* */ }
                throw new Error(msg);
            }
            const blob = await res.blob();
            const dl = document.createElement("a");
            dl.href = URL.createObjectURL(blob);
            const cd = res.headers.get("Content-Disposition") || "";
            const m = cd.match(/filename="([^"]+)"/);
            dl.download = m ? m[1] : `${employee.id}.pdf`;
            dl.click();
            setTimeout(() => URL.revokeObjectURL(dl.href), 1500);
        } catch (e) {
            showBanner("error", `Download failed: ${e.message}`);
        } finally {
            setDownloadingPdf(false);
        }
    };

    // Attachment download helper - JWT-gated stream from /attachment/:id.
    const downloadAttachment = async (id, filename) => {
        try {
            const res = await apiFetch(`/api/dspr/review/attachment/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                let msg = "Couldn't download attachment";
                try { const j = await res.json(); msg = j.error || msg; } catch { /* */ }
                throw new Error(msg);
            }
            const blob = await res.blob();
            const dl = document.createElement("a");
            dl.href = URL.createObjectURL(blob);
            dl.download = filename || "attachment";
            dl.click();
            setTimeout(() => URL.revokeObjectURL(dl.href), 1500);
        } catch (e) {
            showBanner("error", `Download failed: ${e.message}`);
        }
    };

    return (
        <div className="dspr-rf-card">
            <div className="dspr-rf-card-head">
                <div className="dspr-rf-eyebrow">Submitted on {fmtDateShort(session.review.submitted_at)}</div>
                <h2 className="dspr-rf-card-h2">Performance self review</h2>
                <p className="dspr-rf-card-sub">This is a read-only view of your submitted review. Contact <a href="mailto:admin@dolluzcorp.com" className="dspr-rf-mail-link">admin@dolluzcorp.com</a> if you need to update anything.</p>
            </div>
            <div className="dspr-rf-card-body">
                {/* Inline banner - replaces Swal popups for the download flows. */}
                <Banner kind={banner?.kind} message={banner?.message}
                        onDismiss={() => setBanner(null)} />
                {/* Re-download bar - button only present if employee opted
                    in for the PDF copy at submit time. The "● Submitted"
                    badge always shows. */}
                <div className="dspr-rf-readonly-actions">
                    <span className="dspr-rf-readonly-tag">
                        ● Submitted
                    </span>
                    <div className="dspr-rf-readonly-spacer" />
                    {session?.review?.wants_pdf && (
                        <button type="button"
                            className="dspr-rf-btn dspr-rf-btn-primary"
                            onClick={downloadPdf}
                            disabled={downloadingPdf}>
                            {downloadingPdf ? "Downloading…" : "📥 Re-download PDF"}
                        </button>
                    )}
                </div>

                <div className="dspr-rf-ident">
                    <IdField auto label="Employee">{`${employee.first_name} ${employee.last_name}`.trim()}</IdField>
                    <IdField auto label="Employee ID">{employee.id}</IdField>
                    <IdField auto label="Designation">{employee.job_position || "-"}</IdField>
                    <IdField auto label="Department">{employee.department || "-"}</IdField>
                    <IdField auto label="Home Manager">{manager ? `${manager.first_name} ${manager.last_name}` : "-"}</IdField>
                    <IdField label="Client / Project">{clientProjectDisplay}</IdField>
                </div>
                {DSPR_QUESTION_SCHEMA.sections.map(section => (
                    <SectionCard
                        key={section.id}
                        section={section}
                        responses={responses}
                        attachmentsByQ={attachmentsByQ}
                        onUpdate={() => { }}
                        readOnly
                        downloadAttachment={downloadAttachment}
                    />
                ))}
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// PAGE 3 - THANK YOU
// ════════════════════════════════════════════════════════════════════════════
function ThankYouPage({ session, thankYou }) {
    const employeeName = thankYou?.employee_name
        || (session?.employee ? `${session.employee.first_name} ${session.employee.last_name}`.trim() : "you");
    const halfLabel = thankYou?.half_label || session?.cycle?.half_label || "";
    // Count answered questions from the schema. Falls back to "your review"
    // if responses aren't available in the thank-you payload.
    const answeredCount = thankYou?.answered_count
        ?? (thankYou?.responses
            ? DSPR_QUESTION_SCHEMA.sections.flatMap(s => s.questions).filter(q => isAnswered(q, thankYou.responses[q.id])).length
            : null);
    // Formatted submission date - server timestamp first, else now.
    const submittedAt = thankYou?.submitted_at ? new Date(thankYou.submitted_at) : new Date();
    const submittedAtStr = fmtDateLong(submittedAt);

    return (
        <div className="dspr-rf-card dspr-rf-card-compact dspr-rf-thanks-card">
            <div className="dspr-rf-thanks">
                <div className="dspr-rf-thanks-seal">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                    </svg>
                </div>
                <h2 className="dspr-rf-thanks-title">Review Submitted!</h2>
                <p className="dspr-rf-thanks-lead">
                    Thank you for completing the self-performance review for
                    <br />
                    {halfLabel ? <strong>{halfLabel}</strong> : null}.
                    <br />
                    Your response has been received by the Dolluz Corp team.
                </p>

                {/* Stats - 3 rows: criteria count, date, link expired */}
                <div className="dspr-rf-thanks-stats">
                    {answeredCount != null && (
                        <div className="dspr-rf-thanks-stat-row">
                            <span aria-hidden="true">📋</span>
                            <span>{answeredCount} {answeredCount === 1 ? "question" : "questions"} answered</span>
                        </div>
                    )}
                    <div className="dspr-rf-thanks-stat-row">
                        <span aria-hidden="true">📅</span>
                        <span>Submitted: {submittedAtStr}</span>
                    </div>
                    <div className="dspr-rf-thanks-stat-row">
                        <span aria-hidden="true">🔒</span>
                        <span>This link has now expired</span>
                    </div>
                </div>

                <div className="dspr-rf-thanks-contact">
                    For any amendments or queries, please contact
                    <br />
                    <a href="mailto:admin@dolluzcorp.com" className="dspr-rf-mail-link">admin@dolluzcorp.com</a>
                </div>

                <div className="dspr-rf-thanks-brand">
                    <div className="dspr-rf-thanks-brand-name">
                        Dolluz Corp<span style={{ color: "#E8520A" }}>.</span>
                    </div>
                    <div className="dspr-rf-thanks-brand-tag">PERFORMANCE SELF REVIEW</div>
                </div>
            </div>
        </div>
    );
}
